import React, { Fragment } from 'react';
import {
  ApiMatchSource,
  ApiMatchSourceType,
  ApiSanctionEntityType,
  ApiSanctionFilter,
  ApiSanctionHit,
  ApiScreeningGoodlistReference,
  ApiScreeningHitClearanceState,
  ApiScreeningMatchProvider,
} from '../../../api';
import ClearanceDetails from './ClearanceDetails';
import { Link } from 'react-router-dom';
import Match from './Match';
import MatchExtraInformation from './MatchExtraInformation';
import { parseJsonSilent } from '../../../common/helper';
import PoweredByDowJones from '../../dow-jones/powered-by-dow-jones';
import { InternalApiCustomListSchemaField } from '../../../api-internal';
import ScreeningListMatch from './ScreeningListMatch';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../../../organisation/feature/helper';
import ScreeningAiInsight from './ScreeningAiInsight';
import { useMe } from '../../../organisation/useMe';
import { SalvAccordion, Size } from '@salvapp/ui-components';
import UserEventTracker, { aiInsightVersion, eventType, FeedbackRating } from '../../../tracking/userEventTracker';

export const isHitFiltered = (hit: ApiSanctionHit) =>
  hit.filters.length > 0 || hit.clearances.length > 0 || hit.goodlistReference;

interface HitsProps {
  hits: ApiSanctionHit[];
  entityType: ApiSanctionEntityType;
  matchSources: ApiMatchSource[];
  schemaFields: InternalApiCustomListSchemaField[];
  customListId?: string;
  screenedValue?: string;
}

export const Hits = ({ hits, entityType, matchSources, schemaFields, customListId, screenedValue = '' }: HitsProps) => {
  const { features } = useMe();

  const matchSourceColors: {
    [key: number]: string;
  } = matchSources.reduce((holder, it) => ({ ...holder, [it.id || -1]: matchSourceColor(it.type) }), {});
  const tdClass = hits.find((hit: ApiSanctionHit) => isHitFiltered(hit)) ? 'border-top-0' : '';
  return hits.map((hit: ApiSanctionHit, index) => {
    const giveFeedbackForAiInsight = async (rating: FeedbackRating, explanation: string | undefined) => {
      return await UserEventTracker.trackUserEvent(eventType.AI_INSIGHT_FEEDBACK, {
        rating,
        explanation,
        inputName: screenedValue,
        targetName: hit.highlight,
        hitId: hit.id,
        version: aiInsightVersion,
      });
    };

    const firstIndexWithAiInsight = hits.findIndex((hit) => hit.aiInsight);

    return (
      <Fragment key={hit.id}>
        {hit.filters.length > 0 && <MatchFilters filters={hit.filters} index={index} />}
        {hit.clearances.length > 0 && <MatchClearances clearances={hit.clearances} index={index} />}
        {hit.goodlistReference && <MatchGoodlistReference goodlistReference={hit.goodlistReference} index={index} />}
        <MatchRow
          hit={hit}
          entityType={entityType}
          index={index}
          highlightColor={matchSourceColors[hit.matchSourceId || -1]}
          listSchemaFields={schemaFields}
          tdClass={tdClass}
          customListId={customListId}
        />
        <ExtraInformation
          hit={hit}
          index={index}
          highlightColor={matchSourceColors[hit.matchSourceId || -1]}
          listSchemaFields={schemaFields}
        />
        {isFeatureEnabled(ORGANISATION_FEATURE.SHOW_AI_INSIGHTS_FOR_SCREENING_HITS, features) && hit.aiInsight && (
          <ScreeningAiInsight
            index={index}
            isFirst={index === firstIndexWithAiInsight}
            hit={hit}
            aiInsight={hit.aiInsight}
            giveFeedbackForAiInsight={giveFeedbackForAiInsight}
          />
        )}
      </Fragment>
    );
  });
};

interface MatchFiltersProps {
  filters: ApiSanctionFilter[];
  index: number;
}

export const MatchFilters = ({ filters, index }: MatchFiltersProps) => {
  return (
    <tr className={`custom-table-row-${index % 2}`}>
      <td colSpan={4}>
        Filtered by:&nbsp;
        <span className='link-color'>{filters.map((filter) => filter.matchField).join(', ')}</span>
      </td>
    </tr>
  );
};

interface MatchClearancesProps {
  clearances: ApiScreeningHitClearanceState[];
  index: number;
}

export const MatchClearances = ({ clearances, index }: MatchClearancesProps) => {
  return (
    <tr className={`custom-table-row-${index % 2}`}>
      <td colSpan={4}>
        <span className='link-color'>
          {clearances.map((clearance, index) => (
            <>
              <ClearanceDetails key={index} clearance={clearance} />
              <br />
            </>
          ))}
        </span>
      </td>
    </tr>
  );
};

interface MatchGoodlistReferenceProps {
  goodlistReference: ApiScreeningGoodlistReference;
  index: number;
}

export const MatchGoodlistReference = ({ goodlistReference, index }: MatchGoodlistReferenceProps) => {
  return (
    <tr className={`custom-table-row-${index % 2}`}>
      <td colSpan={4}>
        <span className='link-color'>
          <Link to={`/screening/alerts/${goodlistReference.sanctionAlertId}`}>
            Goodlisted
            {goodlistReference.details && `(${goodlistReference.details})`}. Source alert: #
            {goodlistReference.sanctionAlertId}
          </Link>
        </span>
      </td>
    </tr>
  );
};

interface MatchRowProps {
  hit: ApiSanctionHit;
  entityType: ApiSanctionEntityType;
  index: number;
  highlightColor: string;
  listSchemaFields: InternalApiCustomListSchemaField[];
  tdClass?: string;
  customListId?: string;
}

export const MatchRow = ({
  hit,
  entityType,
  index,
  highlightColor,
  listSchemaFields,
  tdClass,
  customListId,
}: MatchRowProps) => {
  const properties = hit.record?.summary ? parseJsonSilent(hit.record.summary) : parseJsonSilent(hit.matchData);
  const hitId = hit.record?.externalId || properties.id;
  return (
    <tr className={`custom-table-row-${index % 2}`}>
      <td className={`match ${tdClass}`}>
        {hit.matchProvider !== ApiScreeningMatchProvider.OTHER && (
          <Match
            properties={properties.properties}
            highlight={hit.highlight}
            highlightColor={highlightColor}
            type={properties.type}
          />
        )}
        {hit.matchProvider === ApiScreeningMatchProvider.OTHER && (
          <ScreeningListMatch
            entityType={entityType}
            properties={properties}
            schemaFields={listSchemaFields?.filter((f) => !!f.parentId || f.visibleInMatches)}
            highlight={hit.highlight?.replaceAll(/[[\]]/g, '')}
            highlightColor={highlightColor}
            screenablePath={hit.screenablePath}
          />
        )}
      </td>
      <td className={tdClass}>{hit.score !== 0 ? hit.score : `-`}</td>
      <td className={tdClass}>
        {customListId ? (
          <Link to={`/custom-lists/${customListId}/`}>{hit.sanctionList}</Link>
        ) : (
          <span>{hit.sanctionList}</span>
        )}
        <br />
        <span>
          Record ID:{' '}
          {customListId ? (
            <Link to={`/custom-lists/${customListId}/records/${hitId}`}>{hitId}</Link>
          ) : (
            <span>{hitId}</span>
          )}
        </span>
        {hit.matchProvider === ApiScreeningMatchProvider.DOW_JONES && <PoweredByDowJones />}
      </td>
    </tr>
  );
};

interface ExtraInformationProps {
  hit: ApiSanctionHit;
  index: number;
  highlightColor: string;
  listSchemaFields?: InternalApiCustomListSchemaField[];
}

export const ExtraInformation = ({ hit, index, highlightColor, listSchemaFields }: ExtraInformationProps) => {
  const properties = hit.record?.summary ? parseJsonSilent(hit.record.summary) : parseJsonSilent(hit.matchData);
  return (
    <tr className={`custom-table-row-${index % 2}`}>
      <td colSpan={4}>
        <SalvAccordion label={'More details'} size={Size.SMALL}>
          {hit.matchProvider !== ApiScreeningMatchProvider.OTHER &&
            Object.prototype.hasOwnProperty.call(properties, 'recordDate') && (
              <MatchExtraInformation
                index={index}
                highlightColor={highlightColor}
                matchData={hit.matchData}
                highlight={hit.highlight}
                record={hit.record}
              />
            )}
          {hit.matchProvider !== ApiScreeningMatchProvider.OTHER &&
            !Object.prototype.hasOwnProperty.call(properties, 'recordDate') && (
              <MatchExtraInformation
                index={index}
                highlightColor={highlightColor}
                matchData={hit.matchData}
                highlight={hit.highlight}
              />
            )}
          {hit.matchProvider === ApiScreeningMatchProvider.OTHER && (
            <ScreeningListMatch
              properties={properties}
              schemaFields={listSchemaFields?.filter((f) => !!f.parentId || !f.visibleInMatches)}
              highlight={hit.highlight}
              highlightColor={highlightColor}
              fieldNames={listSchemaFields?.filter((f) => !f.parentId)?.map((f) => f.fieldName)}
              showUnMappedProperties={true}
            />
          )}
        </SalvAccordion>
      </td>
    </tr>
  );
};

export const matchSourceColor = (input: ApiMatchSourceType) => {
  switch (input) {
    case ApiMatchSourceType.ORIGINAL:
      return 'yellow';
    case ApiMatchSourceType.RFI:
      return '#ecbbfb';
    default:
      return 'yellow';
  }
};
